import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router';
import track from 'react-tracking';
import sessionStore from 'client/Session/SessionStore';
import {Set, OrderedSet} from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {getModelForResourceType} from 'resources/modelRegistry';
import PracticeView from 'components/PracticeView/PracticeView.react';
import TitleBar from 'components/PracticeView/TitleBar/TitleBar.react';
import QuestionsListHeader from 'components/PracticeView/QuestionsListHeader/QuestionsListHeader.react';
import LoadingView from 'components/PracticeView/LoadingView/LoadingView.react';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {hasSubjectAccess} from 'lib/UserAccessUtil';
import {resource, queryBuilderPropType} from '@albert-io/json-api-framework/request/builder';

import {removeQueryParams} from 'client/history';
import HeadingActions from 'components/PracticeView/HeadingActions/HeadingActions.react';
import QuestionActions from 'components/PracticeView/QuestionActions/QuestionActions.react';
import FoldersModal from 'components/PracticeView/FoldersModal/FoldersModal.react';
import {getQuestionEditPath} from 'client/Dennis/Content/Queue/shared';
import PaginatedResultsManager from 'components/PracticeView/helpers/paginatedResultsManager';
import {
  getPreviousQuestionState,
  getNextQuestionState
} from 'components/PracticeView/helpers/questionNavigation.utils';
import {
  hasEditPermissions,
  canRevealSolution,
  hasMoreQuestionInfo,
  hasPreviousAttempts
} from 'components/PracticeView/practiceView.utils';
import folderIcon from 'client/components/PracticeView/StagingDetails/folder-open.svg';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import {addGenericErrorToast, SortableContainerProvider} from '@albert-io/atomic';
import {
  applyFilters,
  hasActiveFilter
} from 'components/PracticeView/PracticeViewToolbar/Filters/filters.utils';

import {MobilePracticeViewProvider} from 'components/PracticeView/MobilePracticeViewProvider.context';

import FolderPracticeViewEmptyState from './EmptyState/FolderPracticeViewEmptyState.react';

import {
  getFolder,
  getFolderQuestionSetsQuery,
  getFolderQuestionSets
} from './folderPracticeView.queries';
import FolderDetails from './FolderDetails/FolderDetails.react';
import FolderToolbar from './FolderToolbar/FolderToolbar.react';

import {FolderPracticeViewQuestionList} from './QuestionList/FolderPracticeViewQuestionList.react';

import './folder-practice-view.scss';

@track({feature: 'Folder Practice View'})
class FolderPracticeView extends React.Component {
  static propTypes = {
    questionSets: ImmutablePropTypes.list,
    isLoading: PropTypes.bool,
    folder: PropTypes.instanceOf(getModelForResourceType('template_v1')),
    questionSetsQuery: queryBuilderPropType,
    isOwner: PropTypes.bool,
    location: PropTypes.object,
    triggerRerender: PropTypes.func,
    router: PropTypes.object,
    folderFound: PropTypes.bool
  };

  constructor(props) {
    super(props);
    const {
      activeQuestionSet,
      activeQuestion,
      questionCount,
      questionSetsCount,
      paginatedResultsManager
    } = this.getBootstrappedValues();
    this.state = {
      activeGuess: null,
      selectedQuestionSets: OrderedSet(),
      isSelectAllQuestionSetsMode: false,
      showFoldersModal: false,
      openedModalFromHeading: false,
      isRearranging: false,
      isViewingFolderDetails: !props.isOwner,
      questionSetsToAddToFolder: null,
      isFetchingFilteredSets: false,
      activeQuestionSet,
      activeQuestion,
      questionCount,
      questionSetsCount
    };

    this.paginatedQuestionsListManager = paginatedResultsManager;
    this.scrollToQuestionById = React.createRef();
  }

  async componentDidUpdate(prevProps) {
    if (
      !prevProps.questionSetsQuery.equals(this.props.questionSetsQuery) ||
      this.props.questionSets !== prevProps.questionSets
    ) {
      await this.props.questionSetsQuery.getResourcePromise();
      const {
        activeQuestionSet,
        activeQuestion,
        questionCount,
        questionSetsCount,
        paginatedResultsManager
      } = this.getBootstrappedValues();
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        activeQuestionSet,
        activeQuestion,
        questionSetsCount,
        questionCount
      });
      this.paginatedQuestionsListManager = paginatedResultsManager;
    }
  }

  getBootstrappedValues = () => {
    if (!this.props.folderFound) {
      return {
        activeQuestionSet: null,
        activeQuestion: null,
        paginatedResultsManager: null,
        questionCount: 0,
        questionSetsCount: 0
      };
    }
    const newPaginatedResultsManager = new PaginatedResultsManager({
      initialQuery: this.props.questionSetsQuery,
      onUpdate: (incomingQuestionSets) => {
        if (!incomingQuestionSets) {
          this.forceUpdate();
        }
      }
    });

    const firstQuestionSet = newPaginatedResultsManager.getResults().first() || null;
    const firstQuestion = firstQuestionSet?.getQuestions().first() || null;
    const questionSetMeta = this.props.questionSetsQuery.getResourceMetadata();
    const questionCount = questionSetMeta.getIn(['request', 'count_of_questions']);
    const questionSetsCount = questionSetMeta.getIn(['page', 'total_entries'], 0);

    return {
      activeQuestionSet: firstQuestionSet,
      activeQuestion: firstQuestion,
      paginatedResultsManager: newPaginatedResultsManager,
      questionCount,
      questionSetsCount
    };
  };

  // Is used to reset questions list after sorting
  invalidateQuestionsList = () => {
    invalidatePartialInterest({resourcePath: this.props.questionSetsQuery.done().resourcePath});
  };

  // Passed to the toolbar and is used when cancelling out of sort
  resetQuestionsListOrder = () => {
    this.invalidateQuestionsList();
    this.paginatedQuestionsListManager = new PaginatedResultsManager({
      initialQuery: this.props.questionSetsQuery,
      onUpdate: (incomingQuestionSets) => {
        if (!incomingQuestionSets) {
          this.forceUpdate();
        }
      }
    });
  };

  // return set of subjects in the folder
  getSubjectsInFolder = () => {
    return this.props.folder.getQuestionSets().reduce((acc, questionSet) => {
      return acc.includes(questionSet.getSubject()) ? acc : acc.add(questionSet.getSubject());
    }, Set());
  };

  hasAccessToAllSubjects = () => {
    let hasAccess = true;
    [...this.getSubjectsInFolder()].forEach((subject) => {
      const {isReady, value} = hasSubjectAccess(subject.getId());
      if (isReady && !value) {
        hasAccess = false;
      }
    });
    return hasAccess;
  };

  // taken directly from SPV
  getSelectedQuestionCount() {
    const {selectedQuestionSets, isSelectAllQuestionSetsMode} = this.state;
    const selectedCount = selectedQuestionSets.reduce((acc, id) => {
      return acc + this.paginatedQuestionsListManager.getQuestionSetById(id).getQuestions().size;
    }, 0);
    return isSelectAllQuestionSetsMode ? this.state.questionCount - selectedCount : selectedCount;
  }

  // taken directly from SPV
  getHeadingActions() {
    const {activeQuestion} = this.state;
    if (!activeQuestion || this.paginatedQuestionsListManager.getResults().size === 0) {
      return null;
    }

    const actions = [];
    if (hasPreviousAttempts(activeQuestion)) {
      actions.push(
        <HeadingActions.PreviousAttempts
          key='PreviousAttempts'
          onGuessClick={this.setActiveGuess}
        />
      );
    }
    if (hasEditPermissions(activeQuestion)) {
      const subjectId = this.paginatedQuestionsListManager
        .getResults()
        .first()
        .getSubject()
        .getId();
      const questionSetId = this.state.activeQuestionSet.getId();
      actions.push(
        <HeadingActions.EditQuestionItem
          key='EditQuestionItem'
          editPath={getQuestionEditPath({
            questionSetId: this.state.activeQuestionSet.getId(),
            questionId: activeQuestion.getId(),
            customSearch: {
              status: 'published',
              subject: subjectId,
              page: questionSetId,
              fallback_page: 1
            }
          })}
        />
      );
    }
    if (sessionStore.hasValidSession()) {
      actions.push(<HeadingActions.QuestionFeedbackItem key='QuestionFeedbackItem' />);
    }
    actions.push(
      <HeadingActions.SaveToItem key='SaveToItem' onClick={this.toggleFoldersModalFromHeading} />
    );

    return !!actions.length && <HeadingActions>{actions}</HeadingActions>;
  }

  getFooterActions() {
    const {activeQuestion, activeGuess, activeQuestionSet} = this.state;
    const subject = activeQuestionSet.getSubject();

    const actions = [];
    let key = 0;
    if (hasMoreQuestionInfo()) {
      actions.push(
        <QuestionActions.MoreInfo
          key={(key += 1)}
          questionSetId={activeQuestionSet.getId()}
          userId={masqueradeStore.getUserIdByMasqueradeState()}
        />
      );
    }
    if (
      canRevealSolution({
        question: activeQuestion,
        questionSet: activeQuestionSet,
        subject
      })
    ) {
      actions.push(
        <QuestionActions.ToggleViewSolution
          key={(key += 1)}
          question={activeQuestion}
          isViewingSolution={!!activeGuess}
          handleToggleSolution={activeGuess ? this.resetGuess : this.setActiveGuess}
        />
      );
    }
    if (hasEditPermissions(activeQuestion)) {
      actions.push(
        <QuestionActions.EditQuestionLink
          key={(key += 1)}
          question={activeQuestion}
          questionSet={activeQuestionSet}
        />
      );
    }
    return (
      <QuestionActions questionStoreName={activeQuestion.getStore().getName()}>
        {actions}
      </QuestionActions>
    );
  }

  // taken directly from SPV
  makeQuestionSetOnChangeHandler = (questionSet) => {
    return () => {
      const questionSetId = questionSet.getId();
      this.setState((state) => {
        const newSelectedSets = state.selectedQuestionSets.includes(questionSetId)
          ? state.selectedQuestionSets.delete(questionSetId)
          : state.selectedQuestionSets.add(questionSetId);

        const isDeselectingLastSelectAllSet =
          state.isSelectAllQuestionSetsMode && newSelectedSets.size === state.questionSetsCount;
        return isDeselectingLastSelectAllSet
          ? {
              selectedQuestionSets: OrderedSet(),
              isSelectAllQuestionSetsMode: false
            }
          : {
              selectedQuestionSets: newSelectedSets
            };
      });
    };
  };

  toggleSelectAllQuestionSets = (e) => {
    this.setState({
      isSelectAllQuestionSetsMode: e.target.checked,
      selectedQuestionSets: OrderedSet()
    });
  };

  toggleFoldersModal = () => {
    if (this.state.showFoldersModal) {
      this.setState({
        showFoldersModal: false,
        openedModalFromHeading: false
      });
    } else {
      this.setQuestionSetIdsForAddToFolder();
      this.setState({
        showFoldersModal: true
      });
    }
  };

  toggleFoldersModalFromHeading = () => {
    this.setState({
      openedModalFromHeading: true
    });
    this.toggleFoldersModal();
  };

  toggleRearranging = () => {
    removeQueryParams();
    this.setState((state) => ({
      isRearranging: !state.isRearranging
    }));
  };

  toggleFolderDetails = () => {
    this.setState({isViewingFolderDetails: false});
  };

  resetGuess = () => {
    global.scrollTo(0, 0);
    this.setState({activeGuess: null});
  };

  setActiveGuess = (guess) => {
    global.scrollTo(0, 0);
    this.setState({activeGuess: guess});
  };

  setActiveQuestionState = (activeQuestion, activeQuestionSet) => {
    this.setState({
      activeQuestion,
      activeQuestionSet
    });
  };

  clearQuestionSetSelections = () => {
    this.setState({
      selectedQuestionSets: OrderedSet(),
      isSelectAllQuestionSetsMode: false
    });
  };

  // taken directly from SPV
  makeNavigateBackProps = () => {
    const questionSets = this.paginatedQuestionsListManager.getResults();
    if (questionSets.isEmpty()) {
      return null;
    }
    const previousSetAndQuestion = getPreviousQuestionState({
      questionSets,
      activeSet: this.state.activeQuestionSet,
      activeQuestion: this.state.activeQuestion
    });
    return this.makeNavigateProps(previousSetAndQuestion);
  };

  // taken directly from SPV
  makeNavigateNextProps = () => {
    const questionSets = this.paginatedQuestionsListManager.getResults();
    if (questionSets.isEmpty()) {
      return null;
    }
    const nextSetAndQuestion = getNextQuestionState({
      questionSets,
      activeSet: this.state.activeQuestionSet,
      activeQuestion: this.state.activeQuestion
    });
    return this.makeNavigateProps(nextSetAndQuestion);
  };

  // taken directly from SPV
  makeNavigateProps(upcomingSetAndQuestion) {
    if (!upcomingSetAndQuestion) {
      return {disabled: true};
    }
    const {questionSet, question} = upcomingSetAndQuestion;
    return {
      disabled: false,
      handleNavigation: () => {
        this.scrollToQuestionById.current(questionSet.getId());
        this.setActiveQuestionState(question, questionSet);
      }
    };
  }

  onSortEnd = (oldIndex, newIndex) => {
    this.paginatedQuestionsListManager.moveQuestionSetToIndex(oldIndex, newIndex);
    // we don't want to invalidate the resource here since we are not saving any changes yet
    this.forceUpdate();
  };

  getFilteredQuestionSetIds = async () => {
    let questionSetIds = null;
    try {
      // gets all sets that meet filter criteria
      const questionSets = await applyFilters(
        resource('question_sets_v1')
          .mandarkEndpoint(['templates_v1', this.props.folder.getId(), 'question_sets_v1'])
          .pageSize('infinite')
      ).getResourcePromise();
      questionSetIds = questionSets.reduce((acc, set) => {
        // This is an XOR logic statement - if the values are not equal, push the element
        if (
          (this.state.isSelectAllQuestionSetsMode &&
            !this.state.selectedQuestionSets.has(set.getId())) ||
          (!this.state.isSelectAllQuestionSetsMode &&
            this.state.selectedQuestionSets.has(set.getId()))
        ) {
          acc.push(set.getId());
        }
        return acc;
      }, []);
      return questionSetIds;
    } catch (e) {
      addGenericErrorToast();
      return questionSetIds;
    }
  };

  setQuestionSetIdsForAddToFolder = async () => {
    const isFiltering = hasActiveFilter();
    if (this.state.openedModalFromHeading) {
      this.setState((state) => ({questionSetsToAddToFolder: [state.activeQuestionSet.getId()]}));
    } else if (isFiltering && this.state.isSelectAllQuestionSetsMode) {
      this.setState({isFetchingFilteredSets: true});
      try {
        const sets = await this.getFilteredQuestionSetIds();
        this.setState({questionSetsToAddToFolder: sets || null});
      } finally {
        this.setState({isFetchingFilteredSets: false});
      }
    } else {
      this.setState((state) => ({questionSetsToAddToFolder: [...state.selectedQuestionSets]}));
    }
  };

  render() {
    const {folder, isLoading, isOwner, location, triggerRerender, questionSetsQuery, folderFound} =
      this.props;
    const {
      activeQuestion,
      activeQuestionSet,
      activeGuess,
      isSelectAllQuestionSetsMode,
      selectedQuestionSets,
      questionCount,
      questionSetsCount,
      isViewingFolderDetails,
      isRearranging
    } = this.state;

    const hasPreviousHistoryEntry = (global.history?.length || 0) > 1;
    const urlQueries = location.query;
    const isSorting = !!urlQueries.sort;
    const isFiltering = hasActiveFilter();
    let titlebarBackButton = (
      <TitleBar.BackButton as={Link} label='Back to folders list' to='/saved' />
    );
    if (!isOwner && !isViewingFolderDetails) {
      titlebarBackButton = (
        <TitleBar.BackButton
          as='button'
          type='button'
          label='Back to folder overview'
          onClick={() => this.setState({isViewingFolderDetails: true})}
        />
      );
    } else if (hasPreviousHistoryEntry) {
      titlebarBackButton = (
        <TitleBar.BackButton
          as='button'
          type='button'
          label='Back Button'
          onClick={() => this.props.router.goBack()}
        />
      );
    }
    if (!folderFound) {
      return <FolderPracticeViewEmptyState titlebarBackButton={titlebarBackButton} />;
    }
    if (!activeQuestion) {
      return (
        <PracticeView
          toolbar={
            <FolderToolbar
              isLoading={isLoading}
              isFiltering={isFiltering}
              isSorting={isSorting}
              isEmptyPracticeView
            />
          }
          titleBar={
            <TitleBar
              className='u-color_blue-500'
              imgHref={folderIcon}
              title={folder.getName()}
              backButton={titlebarBackButton}
            />
          }
          questionsListProps={{
            header: <QuestionsListHeader heading='No questions found' />,
            noQuestionSetsMessage: 'Your folder is empty or you have filtered out all questions.'
          }}
        />
      );
    }

    const questionSets = this.paginatedQuestionsListManager.getResults();
    const navigateBackProps = this.makeNavigateBackProps();
    const navigateNextProps = this.makeNavigateNextProps();
    const activeSubject = activeQuestionSet.getSubject();
    const selectedQuestionCount = this.getSelectedQuestionCount();
    const toolbarProps = {
      isSelectAllQuestionSetsMode,
      isOwner,
      folder,
      location,
      triggerRerender,
      questionSetsCount,
      selectedQuestionSets,
      questionSets,
      isRearranging,
      isViewingFolderDetails,
      selectedQuestionCount,
      questionCount,
      isSorting,
      isFiltering,
      isLoading,
      invalidateQuestionsList: this.invalidateQuestionsList,
      toggleRearranging: this.toggleRearranging,
      clearQuestionSetSelections: this.clearQuestionSetSelections,
      toggleFoldersModal: this.toggleFoldersModal,
      resetQuestionsListOrder: this.resetQuestionsListOrder,
      getFilteredQuestionSetIds: this.getFilteredQuestionSetIds
    };

    return (
      <SortableContainerProvider onSortEnd={this.onSortEnd}>
        <MobilePracticeViewProvider>
          <PracticeView
            activeQuestion={activeQuestion}
            activeQuestionSet={activeQuestionSet}
            activeSubject={activeSubject}
            activeQuestionStore={activeQuestion && activeQuestion.getStore()}
            activeGuess={activeGuess}
            questionSets={questionSets}
            isLoading={isLoading}
            titleBar={
              <TitleBar
                className='u-color_blue-500'
                imgHref={folderIcon}
                title={folder.getName()}
                backButton={titlebarBackButton}
              />
            }
            toolbar={
              <>
                <FolderToolbar {...toolbarProps} />
                {this.state.showFoldersModal && (
                  <FoldersModal
                    questionSetsQuery={questionSetsQuery}
                    selectedQuestionSetIds={this.state.questionSetsToAddToFolder}
                    folderBeingCopiedId={folder.getId()}
                    userId={sessionStore.getUserId()}
                    closeModalFunc={this.toggleFoldersModal}
                    isSelectAllQuestionSetsMode={isSelectAllQuestionSetsMode}
                    selectedQuestionCount={selectedQuestionCount}
                    isFiltering={isFiltering}
                    isReady={!this.state.isFetchingFilteredSets}
                  />
                )}
              </>
            }
            QuestionListComponent={
              <FolderPracticeViewQuestionList
                activeQuestion={activeQuestion}
                selectedQuestionCount={selectedQuestionCount}
                makeQuestionSetOnChangeHandler={this.makeQuestionSetOnChangeHandler}
                selectedQuestionSets={selectedQuestionSets}
                isSelectAllQuestionSetsMode={isSelectAllQuestionSetsMode}
                isRearranging={isRearranging}
                toggleSelectAllQuestionSets={this.toggleSelectAllQuestionSets}
                scrollToQuestionById={this.scrollToQuestionById}
                onQuestionClick={({question, questionSet}) => {
                  return () => {
                    this.setActiveQuestionState(question, questionSet);
                  };
                }}
                paginatedQuestionsListManager={this.paginatedQuestionsListManager}
                questionSets={questionSets}
                isLoading={isLoading}
              />
            }
            questionRendererProps={{
              actions: this.getFooterActions(),
              rendererType: activeGuess ? 'PostGuess' : 'PreGuess',
              navigateBackProps,
              navigateNextProps,
              headingActions: this.getHeadingActions()
            }}
            stageComponent={
              this.state.isViewingFolderDetails && (
                <FolderDetails
                  folder={folder}
                  toggleFolderDetails={this.toggleFolderDetails}
                  subjects={this.getSubjectsInFolder()}
                />
              )
            }
          />
        </MobilePracticeViewProvider>
      </SortableContainerProvider>
    );
  }
}

export default class BootstrappedFolderPracticeView extends React.Component {
  static propTypes = {
    params: PropTypes.object,
    location: PropTypes.object,
    routeParams: PropTypes.object
  };

  constructor(props) {
    super(props);
    this.state = {
      queryString: JSON.stringify(props.location.query),
      folder: null,
      questionSetsQuery: null,
      isReady: false,
      isLoading: false,
      shouldRerender: false,
      folderFound: false
    };
  }

  componentDidMount() {
    this.setInitialState();
  }

  componentDidUpdate() {
    const latestQueryString = JSON.stringify(this.props.location.query);
    const isNewFolder =
      this.state.folder &&
      !this.state.isLoading &&
      this.state.folder.getId() !== this.props.routeParams.folderId;

    if (isNewFolder || this.state.queryString !== latestQueryString || this.state.shouldRerender) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(
        {
          queryString: latestQueryString,
          isLoading: true,
          isReady: !isNewFolder,
          shouldRerender: false
        },
        this.setInitialState
      );
    }
  }

  setInitialState = async () => {
    const {folderId} = this.props.params;
    let folderFound;
    let folderQuery;
    let questionSets;
    // don't make folder requests if user is a student
    if (!sessionStore.isStudent()) {
      try {
        folderQuery = await getFolder(folderId);
        // This is allowing us to cache the request and give the resultsManager instant access
        // Almost serving the same purpose as using `AwaitMandark`
        // refrencing the result and passing it down is used for children components to know the base query was invalidated
        // by checking that the results of the query changed in addition to the query changing (e.g deleting a question)
        questionSets = await getFolderQuestionSets(folderId);
        folderFound = true;
      } catch (err) {
        folderFound = false;
      }
    } else {
      folderFound = false;
    }
    const questionSetsQuery = getFolderQuestionSetsQuery(folderId);
    this.setState({
      questionSets,
      questionSetsQuery,
      folderFound,
      folder: folderQuery,
      isReady: true
    });

    if (!sessionStore.isStudent()) {
      await questionSetsQuery.getResourcePromise();
    }
    this.setState({
      isLoading: false
    });
  };

  triggerRerender = () => {
    this.setState({
      shouldRerender: true,
      isReady: false
    });
  };

  render() {
    const {folder, questionSetsQuery, isReady, isLoading, folderFound, questionSets} = this.state;
    if (!isReady) {
      return <LoadingView />;
    }
    const isOwner = sessionStore.getUserId() === folder?.getMeta().getOwnerId();
    return (
      <FolderPracticeView
        {...this.props}
        folderFound={folderFound}
        triggerRerender={this.triggerRerender}
        folder={folder}
        questionSets={questionSets}
        questionSetsQuery={questionSetsQuery}
        isLoading={isLoading}
        isOwner={isOwner}
      />
    );
  }
}
